import { useEffect, useRef, useState } from 'react';
import {
  Anchor,
  Badge,
  Button,
  Card,
  FileInput,
  Group,
  NumberInput,
  Select,
  Stack,
  Table,
  Text,
  Title,
} from '@mantine/core';
import type { WorkHoursImportReport } from '@isg/shared-types';
import {
  useCalismaSaatiImport,
  useCalismaSaatiYaz,
  useCalismaSaatleri,
  useOlayOptions,
  useSahaGunu,
} from '../api/queries';
import { CALISMA_SAATI_IMPORT_ETIKET } from '../i18n/tr';
import { BosListeNotu, QueryStatus } from './parts';
import { ANLAM } from '../theme';
import { ayGoster } from '../lib/format';

/** Şablon boş CSV'dir (başlık satırı; ayraç ';' — TR Excel varsayılanı).
 *  Kolon sırası SABİT, sunucu sıraya bakar (work-hours-import.ts). */
const SABLON_CSV = '\uFEFFİşyeri (ad veya SGK sicili);Yıl;Ay;Saat;Yüklenici (opsiyonel)\n';
const SABLON_ADI = 'calisma-saati-sablonu.csv';

/**
 * Çalışma saati (R11-R12 + U9 — Kapsam §16.8.1). ORAN BURADA YOK: saklanan
 * yalnız PAYDADIR; çarpan rapor parametresidir (R12) — bu ekran oran
 * hesaplamaz, göstermez.
 *
 * - Elle giriş ve import AYNI kapıya yazar; DÜZELTME = ÜSTÜNE YAZMA (U9;
 *   kapanmış olayla eşleşen düzeltme sunucuda bildirim üretir — engel değil).
 * - Giriş yetkisi tenant `calisma_saati_giris` listesinden (form görünürlüğü
 *   `options.calismaSaatiGirebilir` — karar sunucuda, ekran taşıyıcı).
 * - TEMSİLCİ portal tarafında AYNI ekranı kullanır: firması OTURUMDAN
 *   türetilir (form firma seçtirmez), listede yalnız kendi firması görünür.
 */
export function CalismaSaatiScreen({ portal = false }: { portal?: boolean }): React.JSX.Element {
  const satirlar = useCalismaSaatleri();
  const options = useOlayOptions();
  const yaz = useCalismaSaatiYaz();

  const [workplaceId, setWorkplaceId] = useState<string | null>(null);
  const [contractorId, setContractorId] = useState<string | null>(null);
  // Madde 302: varsayılan dönem (yıl · ay) SAHA GÜNÜNDEN (sunucudan), tarayıcı
  // gününden değil — gün gelince bir kez dolar, kullanıcı değiştirir.
  const sahaGunu = useSahaGunu();
  const [yil, setYil] = useState<number | string>('');
  const [ay, setAy] = useState<number | string>('');
  useEffect(() => {
    if (!sahaGunu.data || yil !== '' || ay !== '') return;
    const [y, a] = sahaGunu.data.split('-');
    setYil(Number(y));
    setAy(Number(a));
  }, [sahaGunu.data, yil, ay]);
  const [saat, setSaat] = useState<number | string>('');

  const girebilir = options.data?.calismaSaatiGirebilir === true;
  const hazir = workplaceId && yil !== '' && ay !== '' && saat !== '';

  return (
    <Stack>
      <Title order={3}>Çalışma saatleri</Title>
      <Text size="sm" c="dimmed">
        Oran KPI'larının paydası — hesaplanmış oran hiçbir yerde saklanmaz; çarpan rapor
        parametresidir.
      </Text>
      <QueryStatus queries={[satirlar, options]} />
      {girebilir && (
        <Card withBorder data-testid="calisma-saati-form">
          <Group align="end" wrap="wrap">
            <Select
              label="İşyeri"
              searchable
              data-testid="calisma-saati-isyeri"
              data={(options.data?.workplaces ?? []).map((w) => ({
                value: w.id,
                label: `${w.ad} (${w.companyAd})`,
              }))}
              value={workplaceId}
              onChange={setWorkplaceId}
              w={280}
            />
            {/* Temsilcide firma OTURUMDAN türetilir — seçim yalnız iç yüzeyde. */}
            {!portal && (
              <Select
                label="Yüklenici (boş = ana firma)"
                clearable
                searchable
                data-testid="calisma-saati-firma"
                data={(options.data?.contractors ?? []).map((c) => ({
                  value: c.id,
                  label: c.ad,
                }))}
                value={contractorId}
                onChange={setContractorId}
              />
            )}
            <NumberInput
              label="Yıl"
              w={100}
              value={yil}
              onChange={setYil}
              data-testid="calisma-saati-yil"
            />
            <NumberInput
              label="Ay"
              min={1}
              max={12}
              w={80}
              value={ay}
              onChange={setAy}
              data-testid="calisma-saati-ay"
            />
            <NumberInput
              label="Toplam saat"
              min={0}
              w={140}
              value={saat}
              onChange={setSaat}
              data-testid="calisma-saati-saat"
            />
            <Button
              data-testid="calisma-saati-kaydet"
              disabled={!hazir}
              loading={yaz.isPending}
              onClick={() => {
                if (!hazir || !workplaceId) return;
                yaz.mutate(
                  {
                    workplaceId,
                    yil: Number(yil),
                    ay: Number(ay),
                    saat: Number(saat),
                    ...(contractorId ? { contractorId } : {}),
                  },
                  { onSuccess: () => setSaat('') },
                );
              }}
            >
              Kaydet (düzeltme = üstüne yazma)
            </Button>
          </Group>
          {yaz.isError && (
            <Text size="sm" c={ANLAM.engel} role="alert" data-testid="calisma-saati-hata" mt="sm">
              {yaz.error.message}
            </Text>
          )}
        </Card>
      )}
      {girebilir && <IceAktarBolumu />}
      {satirlar.isSuccess && satirlar.data.length === 0 && (
        <BosListeNotu mesaj="Kayıtlı çalışma saati satırı yok." />
      )}
      {satirlar.isSuccess && satirlar.data.length > 0 && (
        <Table striped withTableBorder data-testid="calisma-saati-listesi">
          <Table.Thead>
            <Table.Tr>
              <Table.Th>İşyeri</Table.Th>
              <Table.Th>Dönem</Table.Th>
              <Table.Th>Saat</Table.Th>
              <Table.Th>Taraf</Table.Th>
              <Table.Th>Kaynak</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {satirlar.data.map((satir) => (
              <Table.Tr key={satir.id}>
                <Table.Td>{satir.workplaceAd}</Table.Td>
                <Table.Td>{ayGoster(satir.donem)}</Table.Td>
                <Table.Td>{satir.saat}</Table.Td>
                <Table.Td>{satir.contractorAd ?? 'Ana firma'}</Table.Td>
                <Table.Td>{satir.kaynak === 'elle' ? 'Elle' : 'İçe aktarma'}</Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      )}
    </Stack>
  );
}

/**
 * İçe aktarma (F3B·S5 — §16.8.1): dosya seç → sunucu satır satır işler →
 * sonuç tablosu (PersonelYuklemePaneli deseni). PARSE SUNUCUDADIR — dosya
 * olduğu gibi gider; kısmi başarı satır düzeyinde, yetki istek düzeyinde.
 * Temsilcide yüklenici kolonu sunucuda YOK SAYILIR (işveren oturumdan).
 */
function IceAktarBolumu(): React.JSX.Element {
  const aktar = useCalismaSaatiImport();
  const [dosya, setDosya] = useState<File | null>(null);
  const [rapor, setRapor] = useState<WorkHoursImportReport | null>(null);
  const sablonRef = useRef<HTMLAnchorElement>(null);

  const sablonIndir = (): void => {
    const url = URL.createObjectURL(new Blob([SABLON_CSV], { type: 'text/csv;charset=utf-8' }));
    const a = sablonRef.current;
    if (!a) return;
    a.href = url;
    a.download = SABLON_ADI;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <Card withBorder data-testid="calisma-saati-import">
      <Stack gap="sm">
        <Group justify="space-between">
          <Text fw={600}>Toplu içe aktarma (CSV / XLSX)</Text>
          <Anchor component="button" type="button" size="sm" onClick={sablonIndir}>
            Boş şablonu indir
          </Anchor>
          {/* Gizli bağlantı — şablon indirme hedefi. */}
          <a ref={sablonRef} style={{ display: 'none' }} aria-hidden="true" />
        </Group>
        <Group align="end" wrap="wrap">
          <FileInput
            label="Dosya"
            placeholder="calisma-saatleri.csv / .xlsx"
            accept=".csv,.xlsx"
            clearable
            value={dosya}
            onChange={(secilen) => {
              setDosya(secilen);
              setRapor(null);
            }}
            w={300}
            data-testid="calisma-saati-dosya"
          />
          <Button
            data-testid="calisma-saati-aktar"
            disabled={!dosya}
            loading={aktar.isPending}
            onClick={() => {
              if (!dosya) return;
              aktar.mutate(dosya, { onSuccess: setRapor });
            }}
          >
            İçe aktar
          </Button>
        </Group>
        <Text size="xs" c="dimmed">
          Kolonlar: işyeri (ad veya SGK sicili) · yıl · ay · saat · yüklenici (opsiyonel). Aynı
          dosya ikinci kez yüklenirse satırlar üstüne yazılır (çift kayıt doğmaz).
        </Text>
        {aktar.isError && (
          <Text size="sm" c={ANLAM.engel} role="alert" data-testid="calisma-saati-import-hata">
            {aktar.error.message}
          </Text>
        )}
        {rapor && (
          <Stack gap="xs">
            <Group gap="xs" data-testid="calisma-saati-import-ozet">
              <Badge color={ANLAM.olumlu}>Eklendi: {rapor.yeni}</Badge>
              <Badge color={ANLAM.bilgi}>Güncellendi: {rapor.guncellenen}</Badge>
              <Badge color={rapor.hatali > 0 ? ANLAM.engel : ANLAM.bilgi}>
                Hatalı: {rapor.hatali}
              </Badge>
            </Group>
            <Table striped withTableBorder data-testid="calisma-saati-import-sonuc">
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Satır</Table.Th>
                  <Table.Th>Sonuç</Table.Th>
                  <Table.Th>Açıklama</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {rapor.satirlar.map((satir) => (
                  <Table.Tr key={satir.satir} data-testid="calisma-saati-import-satir">
                    <Table.Td>{satir.satir}</Table.Td>
                    <Table.Td>
                      <Badge color={IMPORT_RENK[satir.sonuc]} variant="light">
                        {CALISMA_SAATI_IMPORT_ETIKET[satir.sonuc] ?? satir.sonuc}
                      </Badge>
                    </Table.Td>
                    <Table.Td>{satir.mesaj ?? '—'}</Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </Stack>
        )}
      </Stack>
    </Card>
  );
}

const IMPORT_RENK: Record<string, string> = {
  yeni: ANLAM.olumlu,
  guncellendi: ANLAM.bilgi,
  hata: ANLAM.engel,
};
